// SEO helpers: absolute URLs, canonical links and JSON-LD.
// SITE_URL is set at build time (see vite.config.ts `define`).
import { resolveSiteUrl } from '~/lib/site/site-url'

export const SITE_URL: string = resolveSiteUrl(import.meta.env.SITE_URL)

export const SITE_NAME = 'Direct Action Everywhere'
export const OG_IMAGE = `${SITE_URL}/images/og-default.jpg`
export const LOGO_URL = `${SITE_URL}/images/logo-dxe-dark.png`

/** Absolute URL for a pathname, without a trailing slash ('/' -> SITE_URL). */
export function absoluteUrl(pathname: string) {
  const path = pathname.replace(/[?#].*$/, '').replace(/\/+$/, '')
  return `${SITE_URL}${path}`
}

/** Plain-text excerpt of an HTML string, cut on a word boundary. */
export function excerpt(html: string | undefined, max = 160) {
  const text = (html ?? '')
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  const space = cut.lastIndexOf(' ')
  return `${cut.slice(0, space > 80 ? space : cut.length)}…`
}

/** A `scripts` entry for a JSON-LD block (safe against `</script>` breakouts). */
export function jsonLd(data: Record<string, unknown>) {
  return {
    type: 'application/ld+json',
    children: JSON.stringify({
      '@context': 'https://schema.org',
      ...data,
    }).replace(/</g, '\\u003c'),
  }
}

/** "2026-10-26T19:00:00-07:00": an instant rendered in the given IANA zone. */
export function isoWithOffset(iso: string, timeZone: string) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'longOffset',
    })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  )
  const offset = /GMT([+-]\d\d:\d\d)/.exec(parts.timeZoneName)?.[1] ?? '+00:00'
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}${offset}`
}

export const organizationRef = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
}

/**
 * Page-level `head()` meta: <title> (site name appended unless already there),
 * description, og:title/og:description and optionally og:image / noindex.
 * og:title defaults to the full title; og:description to the description.
 */
export function pageMeta(opts: {
  title: string
  description?: string
  ogTitle?: string
  ogDescription?: string
  image?: string
  noindex?: boolean
}) {
  const suffix = ` | ${SITE_NAME}`
  const title = opts.title.endsWith(suffix) ? opts.title : opts.title + suffix
  const ogDescription = opts.ogDescription ?? opts.description
  return {
    meta: [
      { title },
      ...(opts.description
        ? [{ name: 'description', content: opts.description }]
        : []),
      ...(opts.noindex ? [{ name: 'robots', content: 'noindex' }] : []),
      { property: 'og:title', content: opts.ogTitle ?? title },
      ...(ogDescription
        ? [{ property: 'og:description', content: ogDescription }]
        : []),
      ...(opts.image ? [{ property: 'og:image', content: opts.image }] : []),
    ],
  }
}

/** `head()` result (meta + JSON-LD) shared by the news and blog detail pages. */
export function articleHead(opts: {
  type: 'NewsArticle' | 'BlogPosting'
  path: string
  title: string
  date: string
  html?: string
  summary?: string
  quote?: string
  /** Image for og:image and JSON-LD (callers pass `thumbnail ?? image`). */
  image?: string
  author?: string
  /** Used for the description when summary, quote and body are all empty. */
  fallbackDescription?: string
}) {
  const description =
    opts.summary ||
    opts.quote ||
    excerpt(opts.html) ||
    opts.fallbackDescription ||
    undefined
  return {
    meta: [
      ...pageMeta({
        title: opts.title,
        description,
        ogTitle: opts.title,
        image: opts.image,
      }).meta,
      { property: 'og:type', content: 'article' },
    ],
    scripts: [
      articleLd({
        type: opts.type,
        url: absoluteUrl(opts.path),
        title: opts.title,
        date: opts.date,
        description,
        image: opts.image,
        author: opts.author,
      }),
    ],
  }
}

function articleLd(opts: {
  type: 'NewsArticle' | 'BlogPosting'
  url: string
  title: string
  date: string
  description?: string
  image?: string
  author?: string
}) {
  return jsonLd({
    '@type': opts.type,
    mainEntityOfPage: opts.url,
    url: opts.url,
    headline: opts.title.slice(0, 110),
    datePublished: opts.date,
    ...(opts.description ? { description: opts.description } : {}),
    image: [opts.image || OG_IMAGE],
    author: opts.author
      ? { '@type': 'Person', name: opts.author }
      : organizationRef,
    publisher: {
      ...organizationRef,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
  })
}
